import { Alert, Button, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader.tsx";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon.tsx";
import type { ReportTemplate } from "../../../../../api/types/svTypes.ts";

interface Props {
  templates: ReportTemplate[];
  doDelete: (templates: ReportTemplate[]) => Promise<ApiResponse<boolean>>;
  onDeleted: () => void;
  onClose: () => void;
}

export default function DeleteTemplateDialog({ templates, doDelete, onClose, onDeleted }: Props) {
  const deleteTemplates = useCallback(() => doDelete(templates), [doDelete, templates]);

  const { request, error, isLoading: deleting } = useRequest(deleteTemplates);

  const handleDeleteTemplates = async () => {
    const [, error] = await request();
    if (!error) {
      onDeleted();
      onClose();
    }
  };
  const isSingleTemplate = templates.length === 1;

  return (
    <PromptDialog open={true} sx={{ width: 514, minHeight: 305 }}>
      <DialogHeader text="Delete Template" disabled={deleting} onClose={onClose} />
      {error && (
        <Alert sx={{ mb: ".5rem", mx: 2.5 }} severity="error">
          An error occurred while deleting the template.
        </Alert>
      )}
      <Stack direction="column" useFlexGap spacing={3} sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
        <Stack useFlexGap spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="h6">
            {isSingleTemplate ? `Delete ${templates?.[0]?.name} template` : `Delete ${templates.length} templates`}
          </Typography>
          <Typography color="textSecondary" sx={{ whiteSpace: "pre-line", textAlign: "center" }}>
            {isSingleTemplate
              ? "Are you sure you want to delete the template? \n This template will be deleted for all companies as well."
              : "Are you sure you want to delete these templates? \n The templates will be deleted for all companies as well."}
          </Typography>
        </Stack>
      </Stack>
      <DialogButtons cancelDisabled={deleting} onCancel={onClose}>
        <Button variant="outlined" color="error" disabled={deleting} loading={deleting} onClick={handleDeleteTemplates}>
          Delete
        </Button>
      </DialogButtons>
    </PromptDialog>
  );
}
